/**
 * Render question text into clean HTML blocks.
 *
 * The bank's question text uses three structural patterns the raw
 * `white-space: pre-wrap` renderer doesn't handle well:
 *   - markdown-style tables (`| Header | Header |\n|---|---|\n| … |`)
 *   - bullet lists (`• item` or `- item`)
 *   - indented key-value cost lists (`  Label:    $1,234`)
 *
 * This component is presentation-only. It does NOT mutate the bank's
 * question text — the parser walks the raw string and emits the same
 * content as structured DOM. The same string round-tripped through
 * `extractPlainText(node)` would equal the input modulo whitespace.
 *
 * Used by Practice.tsx for the question card. Worked solution is left as
 * pre-wrap (deliberate: the user asked us not to touch that path).
 */
import type { ReactNode } from "react";
import { normalizeDashes } from "./normalizeDashes";

interface Block {
  kind: "table" | "list" | "kv" | "prose";
  lines: string[];
}

// Values longer than this in a kv row stack the row (label on its own
// line, value below) instead of side-by-side. Below the threshold the
// row keeps the two-column right-aligned layout. Roughly 40 characters
// is enough room for a label like "Cost per unit" plus a moderate value
// like "$1,234.50" without crushing either side.
const LONG_KV_VALUE_THRESHOLD = 40;

const TABLE_HEADER_RE = /^\|.+\|\s*$/;
const TABLE_SEP_RE = /^\|[\s\-:|]+\|\s*$/;
const BULLET_RE = /^\s*[•\-]\s+/;

function isTableLine(line: string): boolean {
  return TABLE_HEADER_RE.test(line);
}

function isTableSep(line: string): boolean {
  return TABLE_SEP_RE.test(line) && line.includes("-");
}

function isBullet(line: string): boolean {
  return BULLET_RE.test(line);
}

function isKvLine(line: string): boolean {
  // Indented (≥2 leading spaces) with a colon and some content after.
  const m = /^\s{2,}([^:]{1,60}):\s*(.*\S)\s*$/.exec(line);
  return m !== null;
}

function isNumericish(s: string): boolean {
  const t = s.trim().replace(/[(),%$\s]/g, "");
  if (!t) return false;
  return /^-?\d+(?:\.\d+)?$/.test(t) || /^-?\d{1,3}(?:,\d{3})+(?:\.\d+)?$/.test(s.trim());
}

function parseBlocks(text: string): Block[] {
  // Split by blank lines first; each chunk goes through structural detection.
  const chunks = text.split(/\n\s*\n/);
  const blocks: Block[] = [];
  for (const chunk of chunks) {
    if (!chunk.trim()) continue;
    const lines = chunk.split("\n");

    // Markdown table: header | --- | data ...
    if (lines.length >= 3 && isTableLine(lines[0]) && isTableSep(lines[1])) {
      blocks.push({ kind: "table", lines });
      continue;
    }
    // Bullet list (every non-empty line bulleted)
    if (lines.every((l) => isBullet(l) || !l.trim())) {
      blocks.push({ kind: "list", lines: lines.filter((l) => l.trim()) });
      continue;
    }
    // Indented key-value (mostly k:v lines, allow a couple of stragglers)
    const kvCount = lines.filter((l) => l.trim() && isKvLine(l)).length;
    const nonEmpty = lines.filter((l) => l.trim()).length;
    if (kvCount >= 2 && kvCount / nonEmpty >= 0.6) {
      blocks.push({ kind: "kv", lines });
      continue;
    }
    blocks.push({ kind: "prose", lines });
  }
  return blocks;
}

function splitRow(line: string): string[] {
  // Drop leading + trailing pipe, then split.
  return line.replace(/^\|/, "").replace(/\|\s*$/, "").split("|").map((c) => c.trim());
}

function renderTable(lines: string[], key: number): ReactNode {
  const header = splitRow(lines[0]);
  const dataLines = lines.slice(2).filter((l) => l.trim());
  const rows = dataLines.map(splitRow);
  // Decide which columns are numeric: any column where ≥60% of cells look numeric.
  const colCount = header.length;
  const numericCols = new Set<number>();
  for (let c = 0; c < colCount; c++) {
    const total = rows.length;
    if (!total) break;
    const nums = rows.filter((r) => isNumericish(r[c] ?? "")).length;
    if (nums / total >= 0.6) numericCols.add(c);
  }
  return (
    <div key={key} className="qt-table-wrap" role="region" aria-label="Data table">
      <table className="qt-table">
        <thead>
          <tr>
            {header.map((h, i) => (
              <th key={i} className={numericCols.has(i) ? "qt-td--num" : ""}>
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, ri) => (
            <tr key={ri}>
              {r.map((c, ci) => (
                <td key={ci} className={numericCols.has(ci) ? "qt-td--num" : ""}>
                  {c}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function renderList(lines: string[], key: number): ReactNode {
  return (
    <ul key={key} className="qt-list">
      {lines.map((l, i) => (
        <li key={i}>{l.replace(BULLET_RE, "")}</li>
      ))}
    </ul>
  );
}

// When a stacked kv value carries multiple "; "-delimited pieces (e.g.
// "Stage 1: $113,700; Stage 2: $251,000; Stage 3: $238,300"), the
// downstream renderer used to land all of them on one wrapped line --
// hard to scan, especially when the pieces are short numeric facts.
// `splitStackedValue` returns the pieces only when there are ≥2 of
// them; a single-piece value stays as a string so we don't wrap a
// single short value in pointless list chrome.
function splitStackedValue(value: string): string[] | null {
  if (!value.includes("; ")) return null;
  const pieces = value.split(/;\s+/).map((p) => p.trim()).filter(Boolean);
  return pieces.length >= 2 ? pieces : null;
}

function renderKv(lines: string[], key: number): ReactNode {
  // Render the indented cost lists as a 2-column definition list with the
  // value column right-aligned and tabular-nums.
  const items: { label: string; value: string }[] = [];
  for (const raw of lines) {
    const m = /^\s*([^:]{1,80}):\s*(.*\S)\s*$/.exec(raw);
    if (m) items.push({ label: m[1].trim(), value: m[2].trim() });
  }
  if (items.length === 0) {
    return (
      <p key={key} className="qt-prose">
        {lines.join("\n")}
      </p>
    );
  }
  return (
    <dl key={key} className="qt-kv">
      {items.map((it, i) => {
        const stacked = it.value.length > LONG_KV_VALUE_THRESHOLD;
        const rowClass = stacked
          ? "qt-kv__row qt-kv__row--stacked"
          : "qt-kv__row";
        const numericish = isNumericish(it.value) || it.value.includes("$");
        // Only split when stacked. Side-by-side rows are short by
        // definition (under the threshold) and shouldn't grow chrome.
        const pieces = stacked ? splitStackedValue(it.value) : null;
        return (
          <div key={i} className={rowClass}>
            <dt>{it.label}</dt>
            <dd className={numericish ? "qt-kv__val--num" : ""}>
              {pieces ? (
                <ul className="qt-kv__val-list">
                  {pieces.map((p, j) => (
                    <li key={j}>{p}</li>
                  ))}
                </ul>
              ) : (
                it.value
              )}
            </dd>
          </div>
        );
      })}
    </dl>
  );
}

function renderProse(lines: string[], key: number): ReactNode {
  return (
    <p key={key} className="qt-prose">
      {lines.join("\n")}
    </p>
  );
}

export function renderQuestionContent(text: string): ReactNode {
  if (!text) return null;
  // Display-time normalization: em-dashes and en-dashes become
  // hyphens with sensible spacing BEFORE block parsing, so the
  // structural detection (table/list/kv) sees the same string the
  // user will read. The DB content is untouched.
  const normalized = normalizeDashes(text);
  const blocks = parseBlocks(normalized);
  return blocks.map((b, i) => {
    if (b.kind === "table") return renderTable(b.lines, i);
    if (b.kind === "list") return renderList(b.lines, i);
    if (b.kind === "kv") return renderKv(b.lines, i);
    return renderProse(b.lines, i);
  });
}


// ---------------------------------------------------------------------------
// WorkedSolutionText -- emphasize the final-answer line when we can detect
// it unambiguously. Falls back to the raw text if detection is unclear --
// false positives would mis-highlight a step and confuse the student, so
// the bar is "I am SURE this is the answer line" or change nothing.
// ---------------------------------------------------------------------------

// Lines that begin with "Answer:" / "Final answer:" / "Final Answers ="
// (case-insensitive, leading whitespace OK; plural `s` allowed for the
// multi-part case "Final answers: …"). The optional bold-marker pair
// (`**`) matches the markdown-ish bank style some keys use.
const ANSWER_PREFIX_RE = /^\s*(?:\*\*)?\s*(?:final\s+)?answers?\s*[:=\-]/i;

// Value-shaped: contains a $ amount, a "= value" assignment, a clearly
// labeled decision ("Decision:", "ACCEPT", "REJECT", "KEEP", "DROP"),
// or a "Therefore," / "Therefore:" lead-in. Used only as the secondary
// signal (when no Answer:-prefix line exists) and only when the line is
// short and preceded by a blank line.
const ANSWER_VALUE_HINT_RE =
  /(?:\$\s?\d|=\s*[^\s]|\b(?:therefore|decision|conclude|answer)\b|\b(?:ACCEPT|REJECT|KEEP|DROP|MAKE|BUY|SELL|PROCESS\s+FURTHER)\b)/i;

const ANSWER_VALUE_MAX_LEN = 140;


function normalizeDashesLocal(text: string): string {
  // Tiny re-application so callers that hit this helper directly (the
  // worked-solution path doesn't go through renderQuestionContent) get
  // the same em/en-dash treatment as the question stem.
  return normalizeDashes(text);
}


function detectAnswerSplit(text: string): { lead: string; answer: string } | null {
  const lines = text.split("\n");

  // Trailing block = everything after the last blank line in the text.
  // This is the conventional "the conclusion" block in the bank's
  // worked-solution conventions (steps are separated by blank lines;
  // the final block is the closing answer).
  let blockStart = 0;
  for (let i = lines.length - 1; i > 0; i--) {
    if (!lines[i].trim()) { blockStart = i + 1; break; }
  }
  const trailing = lines.slice(blockStart);
  // Empty trailing block (text ends with a blank line) -- treat the
  // block before the trailing blank as the trailing block instead.
  if (trailing.every((l) => !l.trim())) return null;

  // (1) "Answer:" / "Final answer:" prefix anywhere in the trailing
  //     block -> the answer span is the entire trailing block (from
  //     the prefix line to end). The prefix line stays inside the box
  //     so the highlight is self-explanatory.
  for (let i = 0; i < trailing.length; i++) {
    if (ANSWER_PREFIX_RE.test(trailing[i])) {
      const answerStart = blockStart + i;
      const lead = lines.slice(0, answerStart).join("\n").replace(/\s+$/, "");
      const answer = lines.slice(answerStart).join("\n").trim();
      if (answer) return { lead, answer };
      return null;
    }
  }

  // (2) Fallback: trailing block is short (1-2 lines), contains a
  //     value hint, and is genuinely separated from the steps by a
  //     blank line (blockStart > 0). The conservative bits -- short
  //     + value-shaped + blank-separated -- are what keep us from
  //     mis-highlighting a final calculation step.
  if (blockStart === 0) return null;
  const trailingText = trailing.join("\n").trim();
  if (!trailingText) return null;
  if (trailingText.length > ANSWER_VALUE_MAX_LEN) return null;
  if (trailing.filter((l) => l.trim()).length > 2) return null;
  if (!ANSWER_VALUE_HINT_RE.test(trailingText)) return null;
  const lead = lines.slice(0, blockStart).join("\n").replace(/\s+$/, "");
  return { lead, answer: trailingText };
}


export function WorkedSolutionText({ text }: { text: string }) {
  const raw = (text ?? "").toString();
  if (!raw.trim()) return null;
  const normalized = normalizeDashesLocal(raw);
  const split = detectAnswerSplit(normalized);
  if (!split) {
    // Ambiguous -- render the whole thing untouched so the parent's
    // `white-space: pre-wrap` preserves the original layout.
    return <>{normalized}</>;
  }
  return (
    <>
      {split.lead && <span className="qt-worked__lead">{split.lead}</span>}
      <div className="qt-worked__answer">{split.answer}</div>
    </>
  );
}
